import OpenAI from 'openai';
import { analyzeDocumentsWithAssistant } from './openaiAssistantsService';
import { SYSTEM_PROMPTS } from '../config/systemPrompts.js';

// Initialize OpenAI client
const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true,
});

/**
 * Error message mapping for OpenAI API errors
 */
function getErrorMessage(error) {
  if (error instanceof OpenAI?.AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof OpenAI?.PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits or do not have access to this resource.' };
  } else if (error instanceof OpenAI?.RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. You are sending requests too quickly. Please wait a moment and try again.' };
  } else if (error instanceof OpenAI?.InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof OpenAI?.APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your API key and internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

/**
 * Stream teacher chat response using gpt-4o (primary model)
 * @param {Array} messages - Conversation messages
 * @param {Function} onChunk - Callback for streaming chunks
 * @param {Function} onProgress - Callback for progress updates
 * @param {AbortSignal} signal - Optional abort signal for cancellation
 * @returns {Promise<{content: string, model: string}>} Complete response with metadata
 */
export async function streamTeacherChatResponse(
  messages,
  onChunk,
  onProgress,
  signal = null
) {
  const PRIMARY_MODEL = 'gpt-4o';
  
  let fullResponse = '';

  // Teacher Chat System Prompt
  const SYSTEM_PROMPT = SYSTEM_PROMPTS?.['teacher_chat'];

  try {
    onProgress?.({
      status: 'Gerando resposta pedagógica...',
      model: PRIMARY_MODEL
    });

    // Inject system prompt as first message
    const messagesWithSystem = [
      { role: 'system', content: SYSTEM_PROMPT },
      ...messages
    ];

    const stream = await openai?.chat?.completions?.create({
      model: PRIMARY_MODEL,
      messages: messagesWithSystem,
      stream: true,
      max_tokens: 4096,
      temperature: 0.7,
    }, { signal });

    for await (const chunk of stream) {
      if (signal?.aborted) {
        throw new Error('Geração cancelada pelo usuário');
      }

      const content = chunk?.choices?.[0]?.delta?.content || '';
      
      if (content) {
        fullResponse += content;
        onChunk?.(content);
      }
    }

    onProgress?.({
      status: 'Resposta completa',
      model: PRIMARY_MODEL
    });

    return {
      content: fullResponse,
      model: PRIMARY_MODEL
    };

  } catch (error) {
    if (error?.message === 'Geração cancelada pelo usuário') {
      throw error;
    }
    
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error in teacher chat streaming:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Process document files using hybrid approach:
 * - gpt-4o-mini for reading and indexing content
 * - gpt-4o for generating final response using indexed data
 * @param {Array} files - Array of File objects to analyze
 * @param {string} prompt - User's analysis prompt
 * @param {string} conversationId - Current conversation ID
 * @param {Function} onChunk - Callback for streaming chunks
 * @param {Function} onProgress - Callback for progress updates
 * @param {AbortSignal} signal - Optional abort signal for cancellation
 * @param {number} maxWaitTime - Maximum wait time for analysis
 * @returns {Promise<{content: string, model: string, tokensUsed: number}>} Analysis result
 */
export async function processTeacherChatDocuments(
  files,
  prompt,
  conversationId,
  onChunk,
  onProgress,
  signal = null,
  maxWaitTime = 60000
) {
  const INDEXING_MODEL = 'gpt-4o-mini';
  const RESPONSE_MODEL = 'gpt-4o';

  // Teacher Chat Document Analysis System Prompt
  const DOCUMENT_ANALYSIS_SYSTEM_PROMPT = `Você é um assistente educacional especializado em análise de documentos pedagógicos.

**OBJETIVO EMOCIONAL (CRÍTICO):**
Sua análise de documentos DEVE SEMPRE gerar sentimentos de:
- INSPIRAÇÃO: Faça o professor sentir que pode transformar o conteúdo em aulas incríveis
- CLAREZA: Forneça compreensão cristalina sobre o material analisado
- CONFIANÇA: Inspire segurança para usar o material pedagogicamente
- DESEJO DE APLICAR: Crie entusiasmo genuíno para usar o conteúdo em sala

**ANÁLISE DE DOCUMENTOS:**
Quando analisar documentos (PDF, DOCX, TXT, imagens):
1. Extraia os conceitos principais e estrutura do conteúdo
2. Identifique oportunidades pedagógicas e pontos de ensino
3. Sugira como transformar o conteúdo em material didático
4. Forneça insights sobre como apresentar aos alunos
5. Mantenha o tom empático e encorajador

**FORMATO DE RESPOSTA PARA DOCUMENTOS:**
Siga a mesma estrutura de 8 elementos, adaptando para análise documental:
1. Síntese do documento com validação empática
2. Explicação dos conceitos principais encontrados
3. Exemplo de como usar pedagogicamente
4. Técnica para memorizar/ensinar os conceitos
5. Ação prática HOJE (criar atividade, adaptar slide, etc.)
6. Exercício para praticar o uso do material
7. Frase motivacional sobre o potencial do conteúdo
8. Próximo passo específico (criar avaliação, expandir tópico, etc.)

**TIPOS DE ANÁLISE:**
- Para PDFs/DOCX acadêmicos: Extraia conceitos e crie plano de aula
- Para imagens: Analise conteúdo visual e sugira uso didático
- Para textos: Identifique pontos-chave e crie materiais complementares

Sempre mantenha o tom caloroso, empático e inspirador. Faça o professor se sentir EMPODERADO para usar o material de forma pedagógica eficaz.`;

  try {
    onProgress?.({
      status: 'Lendo e indexando documentos com gpt-4o-mini...',
      model: INDEXING_MODEL
    });

    // Step 1: Use gpt-4o-mini for document reading and indexing via Assistants API
    const analysisResult = await analyzeDocumentsWithAssistant(
      files,
      `${DOCUMENT_ANALYSIS_SYSTEM_PROMPT}\n\nSolicitação do professor: ${prompt}`,
      conversationId,
      (progress) => {
        onProgress?.({
          status: `Indexando conteúdo: ${progress}`,
          model: INDEXING_MODEL
        });
      },
      signal,
      maxWaitTime
    );

    const indexedContent = analysisResult?.analysis || '';
    const tokensUsed = analysisResult?.tokensUsed || 0;

    if (!indexedContent) {
      throw new Error('Falha ao indexar o conteúdo dos documentos');
    }

    onProgress?.({
      status: 'Gerando resposta final com gpt-4o...',
      model: RESPONSE_MODEL
    });

    // Step 2: Use gpt-4o to generate final response using indexed data
    const messages = [
      {
        role: 'system',
        content: DOCUMENT_ANALYSIS_SYSTEM_PROMPT
      },
      {
        role: 'user',
        content: `Conteúdo indexado dos documentos:\n\n${indexedContent}\n\nSolicitação do professor: ${prompt}\n\nPor favor, analise o conteúdo e forneça uma resposta pedagógica completa seguindo a estrutura de 8 elementos.`
      }
    ];

    let fullResponse = '';

    const stream = await openai?.chat?.completions?.create({
      model: RESPONSE_MODEL,
      messages: messages,
      stream: true,
      max_tokens: 4096,
      temperature: 0.7,
    }, { signal });

    for await (const chunk of stream) {
      if (signal?.aborted) {
        throw new Error('Geração cancelada pelo usuário');
      }

      const content = chunk?.choices?.[0]?.delta?.content || '';
      
      if (content) {
        fullResponse += content;
        onChunk?.(content);
      }
    }

    onProgress?.({
      status: 'Análise completa',
      model: `${INDEXING_MODEL} + ${RESPONSE_MODEL}`
    });

    return {
      content: fullResponse,
      model: `${INDEXING_MODEL} (indexação) + ${RESPONSE_MODEL} (resposta)`,
      tokensUsed
    };

  } catch (error) {
    if (error?.message === 'Geração cancelada pelo usuário') {
      throw error;
    }
    
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error in teacher chat document processing:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Check if OpenAI API is properly configured and connected
 * @returns {Promise<{connected: boolean, message: string}>} Connection status
 */
export async function checkTeacherChatConnection() {
  try {
    const response = await openai?.models?.list();
    
    if (response?.data?.length > 0) {
      return {
        connected: true,
        message: 'Conectado ao OpenAI com sucesso'
      };
    }
    
    return {
      connected: false,
      message: 'Não foi possível verificar a conexão com OpenAI'
    };
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    return {
      connected: false,
      message: errorInfo?.message
    };
  }
}

export default {
  streamTeacherChatResponse,
  processTeacherChatDocuments,
  checkTeacherChatConnection
};